// Reusable merged-timeline history component.
// Phase 1a: supports entityType='asset' (pre-merged via GET /api/assets/:id/history).
// Phase 2+: entityType='drone' will migrate here, replacing the multi-fetch client-merge
// in DroneFleet/DronePage/DroneHistory.js. For now, 'drone' is unsupported — consumers
// continue using DroneHistory directly. This component just ships the asset path.

import React, { useState, useEffect } from 'react';
import { useApi } from '../../apiHelpers';
import { useToast } from '../Toast/ToastContext';
import { buttonClass } from '../ui/Button';
import Tooltip from '../ui/Tooltip';
import { EmptyState } from '../ui/EmptyState';
import { Panel } from '../ui/Panel';
import { Skeleton, SkeletonGroup } from '../ui/Skeleton';
import FadeIn from '../ui/FadeIn';
import Appear from '../ui/Appear';
import { formatTimestamp } from '../../utils/date-utils';
import { HistoryEntry, HistoryEntryType } from './types';
// The one thing this component still reads out of a lane: the TimeMachine build
// phase vocabulary, which is an asset-extension column's enum (it types
// TimeMachineDetails.build_phase and drives three Assets-side pickers), so it
// belongs there. A PHASE row needs its labels to say more than 'sd_card_ready'.
import { BUILD_PHASE_LABELS, BuildPhase } from '../../Assets/types';

interface Props {
  entityType: 'asset' | 'drone';
  entityId: string;
  refreshKey?: number; // caller bumps this to force a re-fetch (e.g. after raising a flag)
  // Called after a flag row is cleared inline so the parent can refetch the asset
  // (operational_status may flip when the last active flag clears) and bump its own
  // history refreshKey. Optional — when omitted, only the local timeline refetches.
  onMutate?: () => void;
}

// Left-border + background color per timeline item type. Matches DroneHistory's visual
// language so the two stay familiar side-by-side during the migration window.
// Flag rows compute their own style — see flagStyleFor below — because Flag vs OOC
// vs cleared need three distinct treatments.
const ITEM_STYLE: Record<Exclude<HistoryEntryType, 'flag'>, string> = {
  maintenance: 'border-l-blue-500 bg-blue-900/20',
  assignment:  'border-l-green-500 bg-green-900/20',
  barcode:     'border-l-purple-500 bg-purple-900/20',
  rma:         'border-l-primary bg-primary/10',
  phase:       'border-l-indigo-500 bg-indigo-900/20',
};

// Flag rows: active OOC = red, active Flag = yellow, cleared = green left border with
// a faint neutral backdrop. Keeps the historical type readable via the FLAG/OOC label
// chip even after clearing.
function flagStyleFor(active: boolean, statusdesc: 'Flag' | 'OOC'): string {
  if (!active) return 'border-l-green-500 bg-surface-container';
  return statusdesc === 'OOC'
    ? 'border-l-red-500 bg-red-900/20'
    : 'border-l-yellow-500 bg-yellow-900/20';
}

// Default per-type labels. Flag entries override this with statusdesc-aware rendering
// below so OOC entries get a distinct red "OOC" chip instead of the orange "FLAG".
const TYPE_LABEL: Record<HistoryEntryType, { text: string; className: string }> = {
  flag:        { text: 'FLAG',    className: 'text-orange-400' },
  maintenance: { text: 'MAINT',   className: 'text-blue-400' },
  assignment:  { text: 'MOVED',   className: 'text-green-400' },
  barcode:     { text: 'BARCODE', className: 'text-purple-400' },
  rma:         { text: 'RMA',     className: 'text-primary font-bold' },
  phase:       { text: 'PHASE',   className: 'text-indigo-400' },
};

function labelFor(entry: HistoryEntry): { text: string; className: string } {
  if (entry.type === 'flag' && entry.details?.statusdesc === 'OOC') {
    return { text: 'OOC', className: 'text-red-400' };
  }
  return TYPE_LABEL[entry.type];
}

// Render a human-readable description per entry type, pulling from the details JSON shape
// the backend produces in UNION ALL queries.
function describeEntry(entry: HistoryEntry): string {
  const d = entry.details;
  switch (entry.type) {
    case 'flag': {
      // Label already distinguishes FLAG vs OOC, so skip the redundant prefix in the description.
      const descript = d.descript as string;
      const active = d.active as boolean;
      return active ? descript : `${descript} (cleared)`;
    }
    case 'maintenance':
      return d.descript as string;
    case 'assignment': {
      const note = d.note as string | null;
      const from = d.from_location_id;
      const to = d.to_location_id;
      const arrow = from ? `Loc ${from} → Loc ${to}` : `Moved to Loc ${to}`;
      return note ? `${arrow} — ${note}` : arrow;
    }
    case 'barcode': {
      const oldBC = d.old_barcode as string | null;
      const newBC = d.new_barcode as string;
      return oldBC ? `Re-linked: ${oldBC} → ${newBC}` : `Linked: ${newBC}`;
    }
    case 'rma':
      return `RMA ${d.rma_id}: ${d.item_status} — ${d.issue_description}`;
    case 'phase': {
      const from = d.from_phase as string | null;
      const to = d.to_phase as string;
      const fromLabel = from ? (BUILD_PHASE_LABELS[from as BuildPhase] ?? from) : 'created';
      const toLabel = BUILD_PHASE_LABELS[to as BuildPhase] ?? to;
      return `${fromLabel} → ${toLabel}`;
    }
    default:
      return JSON.stringify(d);
  }
}

const EntityHistory: React.FC<Props> = ({ entityType, entityId, refreshKey = 0, onMutate }) => {
  const api = useApi();
  const toast = useToast();
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [localRefresh, setLocalRefresh] = useState(0);
  const [clearingId, setClearingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        if (entityType === 'asset') {
          const data: HistoryEntry[] = await api.get(`/assets/${entityId}/history`);
          if (!cancelled) setEntries(data);
        } else {
          // Drones continue using DroneHistory.js directly in Phase 1a.
          if (!cancelled) setEntries([]);
        }
      } catch (err) {
        if (!cancelled) setEntries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // api deliberately excluded — useApi() returns a new reference each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, refreshKey, localRefresh]);

  const handleClearFlag = async (flagId: string) => {
    setClearingId(flagId);
    try {
      await api.patch(`/asset-flags/${flagId}`, { body: { active: false } });
      toast.success('Flag cleared');
      // Refresh locally so the row's left border flips to green immediately, and notify
      // the parent so the asset's operational_status banner can re-derive.
      setLocalRefresh(n => n + 1);
      onMutate?.();
    } catch {
      toast.error('Failed to clear flag');
    } finally {
      setClearingId(null);
    }
  };

  return (
    <Panel title="History" titleStyle="section" level={3} className="mb-3">
      <FadeIn key={loading ? 'loading' : entries.length === 0 ? 'empty' : 'list'}>
      {loading && (
        <SkeletonGroup label="Loading history" className="space-y-1">
          {[0, 1, 2].map(n => <Skeleton key={n} className="h-12 w-full" />)}
        </SkeletonGroup>
      )}
      {!loading && entries.length === 0 && (
        <EmptyState variant="area">No history yet</EmptyState>
      )}
      {!loading && entries.length > 0 && (
        <ul className="space-y-1 max-h-80 overflow-y-auto">
          {entries.map(entry => {
            const label = labelFor(entry);
            const isFlag = entry.type === 'flag';
            const flagActive = isFlag && entry.details?.active === true;
            const flagStatus = isFlag ? ((entry.details?.statusdesc as 'Flag' | 'OOC') || 'Flag') : 'Flag';
            const itemStyle = isFlag
              ? flagStyleFor(flagActive, flagStatus)
              : ITEM_STYLE[entry.type as Exclude<HistoryEntryType, 'flag'>];
            return (
              <li
                key={`${entry.type}-${entry.id}`}
                className={`p-2 rounded border-l-4 transition-all duration-200 ${itemStyle}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs shrink-0 font-mono ${label.className}`}>{label.text}</span>
                  <div className="flex-1 text-white text-sm truncate" title={describeEntry(entry)}>
                    {describeEntry(entry)}
                  </div>
                  <Appear show={flagActive} className="shrink-0">
                    <Tooltip label="Clear this flag">
                      <button
                        onClick={() => handleClearFlag(entry.id)}
                        disabled={clearingId === entry.id}
                        className={`${buttonClass('quiet', 'sm')} shrink-0`}
                      >
                        {clearingId === entry.id ? '…' : 'Clear'}
                      </button>
                    </Tooltip>
                  </Appear>
                </div>
                <div className="flex items-center gap-2 text-xs text-on-surface-variant">
                  <span className="font-mono">{formatTimestamp(entry.timestamp, 'MMM D, YYYY HH:mm')}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      </FadeIn>
    </Panel>
  );
};

export default EntityHistory;
